import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar, MapPin, Clock, Video, Upload, X, FileVideo, User } from 'lucide-react';

export default function CoachClassRecords({ user }) {
  const [students, setStudents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [formData, setFormData] = useState({
    student_id: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location_id: '',
    notes: '',
    video_urls: []
  });

  useEffect(() => {
    loadStudents();
    loadLocations();
    loadAllRecords();
  }, []);

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users?role=student`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStudents(res.data);
    } catch (error) {
      console.error('載入學生列表失敗:', error);
      toast.error('載入學生列表失敗');
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocations(res.data);
    } catch (error) {
      console.error('載入地點列表失敗:', error);
    }
  };

  const loadAllRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/class-records/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (error) {
      console.error('載入課堂紀錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/class-records/upload-video`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
          }
        });

        uploadedUrls.push(res.data.video_url);
        toast.success(`${file.name} 上傳成功`);
      } catch (error) {
        console.error('上傳失敗:', error);
        toast.error(`${file.name} 上傳失敗`);
      }
    }

    setFormData(prev => ({
      ...prev,
      video_urls: [...prev.video_urls, ...uploadedUrls]
    }));

    setUploading(false);
    setUploadProgress({});
  };

  const handleCreateRecord = async () => {
    if (!formData.student_id) {
      toast.error('請選擇學生');
      return;
    }

    if (!formData.location_id) {
      toast.error('請選擇訓練地點');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      submitData.append('student_id', formData.student_id);
      submitData.append('date', formData.date);
      submitData.append('time', formData.time);
      submitData.append('location_id', formData.location_id);
      submitData.append('notes', formData.notes);
      submitData.append('video_urls', JSON.stringify(formData.video_urls));

      await axios.post(`${API}/coach/class-records`, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('課堂紀錄已創建');
      setShowCreateDialog(false);
      resetForm();
      loadAllRecords();
    } catch (error) {
      console.error('創建課堂紀錄失敗:', error);
      toast.error('創建課堂紀錄失敗');
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: '',
      date: new Date().toISOString().split('T')[0],
      time: '',
      location_id: '',
      notes: '',
      video_urls: []
    });
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      video_urls: prev.video_urls.filter((_, i) => i !== index)
    }));
  };

  const getStudentRecords = (studentId) => {
    return records.filter(r => r.student_id === studentId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">學生課堂紀錄</h1>
            <p className="text-gray-600 mt-1">為學生添加和管理課堂紀錄</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-500 hover:bg-blue-600"
          >
            <Plus size={20} className="mr-2" />
            新增紀錄
          </Button>
        </div>

        {/* Students Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((student) => {
            const studentRecords = getStudentRecords(student.user_id);
            return (
              <div
                key={student.user_id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedStudent(student)}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{student.username}</h3>
                    <p className="text-sm text-gray-500">{student.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-gray-600">課堂紀錄</span>
                  <span className="text-2xl font-bold text-blue-600">{studentRecords.length}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Create Record Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>新增課堂紀錄</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Student Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <User size={16} className="inline mr-2" />
                  選擇學生 *
                </label>
                <Select
                  value={formData.student_id}
                  onValueChange={(value) => setFormData({...formData, student_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇學生" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.user_id} value={student.user_id}>
                        {student.username} - {student.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  日期 *
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Clock size={16} className="inline mr-2" />
                  時間
                </label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <MapPin size={16} className="inline mr-2" />
                  訓練地點 *
                </label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({...formData, location_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇地點" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.location_id} value={location.location_id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">備註</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="課堂備註、學生表現等..."
                  rows={4}
                />
              </div>

              {/* Video Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Video size={16} className="inline mr-2" />
                  上傳影片
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="video-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="video-upload" className="cursor-pointer">
                    <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">點擊上傳影片</p>
                    <p className="text-xs text-gray-400 mt-1">支援多個檔案上傳</p>
                  </label>
                </div>

                {/* Upload Progress */}
                {Object.keys(uploadProgress).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {Object.entries(uploadProgress).map(([filename, progress]) => (
                      <div key={filename} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{filename}</span>
                          <span className="text-blue-600">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Uploaded Videos */}
                {formData.video_urls.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">已上傳的影片 ({formData.video_urls.length})</p>
                    {formData.video_urls.map((url, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded border border-green-200">
                        <div className="flex items-center gap-2">
                          <FileVideo size={20} className="text-green-600" />
                          <span className="text-sm text-gray-700">影片 {index + 1}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVideo(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  取消
                </Button>
                <Button
                  onClick={handleCreateRecord}
                  disabled={uploading}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  創建紀錄
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Student Detail Dialog */}
        {selectedStudent && (
          <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {selectedStudent.username} 的課堂紀錄
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {getStudentRecords(selectedStudent.user_id).length === 0 ? (
                  <p className="text-center text-gray-500 py-8">暫無課堂紀錄</p>
                ) : (
                  getStudentRecords(selectedStudent.user_id)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((record) => (
                      <div key={record.record_id} className="bg-white border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-blue-600" />
                              <span className="font-medium">{record.date}</span>
                            </div>
                            {record.time && (
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-gray-500" />
                                <span className="text-sm text-gray-600">{record.time}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            由 {record.created_by === user.user_id ? '你' : '其他教練'} 創建
                          </span>
                        </div>

                        {record.notes && (
                          <p className="text-sm text-gray-700 mb-3">{record.notes}</p>
                        )}

                        {record.video_urls && record.video_urls.length > 0 && (
                          <div className="grid grid-cols-2 gap-2">
                            {record.video_urls.map((url, index) => (
                              <video
                                key={index}
                                src={url}
                                controls
                                className="w-full rounded border"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
